import React from "react";
import { useAuth } from "../hooks/useAuth";
import { Button, ButtonSize } from "./Button";
import { ButtonVariant } from "../config/colors";
import { useConfirm } from "../hooks/useConfirm";
import { plebsRepository } from "../repositories/PlebsRepository";
import Loader from "../pages/chat/components/Loader";

const EditProfileImages = () => {
    const {pleb, updateCurrentPleb} = useAuth();
    const {openConfirm} = useConfirm();
    const [loadingStates, setLoadingStates] = React.useState<{[key: number]: boolean}>({});

    const setLoadingByIndex = (index: number, isLoading: boolean) => {
        setLoadingStates(prev => ({
            ...prev,
            [index]: isLoading
        }));
    };

    async function handleRemoveImage(e: React.MouseEvent<HTMLDivElement>, index: number): Promise<void> {
        e.stopPropagation();

        openConfirm({
            title: 'Are you sure you want to remove this image?',
            buttons: [
                {label: 'Remove', onClick: () => {
                    setLoadingByIndex(index, true);
                    plebsRepository.removePicture(index)
                        .then((updatedPleb) => {
                            updateCurrentPleb(updatedPleb);
                        })
                        .catch((error) => {
                            console.error('Failed to remove image:', error);
                        })
                        .finally(() => {
                            setLoadingByIndex(index, false);
                        });
                }, variant: ButtonVariant.danger},
                {label: 'Cancel', onClick: () => {
                    setLoadingByIndex(index, false);
                }, variant: ButtonVariant.transparent},
            ]
        });
    }

    async function handleEditImage(index: number): Promise<void> {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/jpeg';
        
        input.onchange = async (e) => {
            setLoadingByIndex(index, true);
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                try {
                    const updatedPleb = await plebsRepository.updatePicture(index, file);
                    updateCurrentPleb(updatedPleb);
                } catch (error) {
                    console.error('Failed to update image:', error);
                } finally {
                    setLoadingByIndex(index, false);
                }
            } else {
                setLoadingByIndex(index, false);
            }
        };
        
        input.click();
    }

    let pictures: (string | null)[] = [...(pleb?.pictures ?? [])];

    if(pictures.length < 3) {
        pictures.push(null);
    }

    return (
        <div className="flex flex-row flex-wrap gap-2 mb-2">
            {
                pictures.map((url, index) => (
                    <div 
                        key={index} 
                        className="flex flex-col items-center gap-1 cursor-pointer relative"
                        onClick={() => !loadingStates[index] && handleEditImage(index)}
                    >
                        <div className="relative">
                            <img 
                                src={url ?? ''} 
                                className="object-cover w-16 h-16 rounded-full border-2 border-brand-orange"
                            />
                            {
                                loadingStates[index] && (
                                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                                        <Loader small />
                                    </div>
                                )
                            }
                        </div>
                        <Button
                            variant={ButtonVariant.transparent}
                            size={ButtonSize.small}
                        >
                            {url ? 'Edit' : 'Add'}
                        </Button>
                        {
                            url && (
                                <div className="absolute -top-3 -right-3 p-2" onClick={(e) => !loadingStates[index] && handleRemoveImage(e, index)}>
                                    <div className="bg-brand-red text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                                        <span className="material-symbols-outlined text-[20px]">
                                            remove
                                        </span>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                ))
            }
        </div>
    )
}

export default EditProfileImages;